import type { ChartPlan } from 'src/charting/plan/chartPlan.types'
import { toReportingStackedBarPlan } from 'src/charting/reporting/reportingStackedBarPlan'
import type { ExtendCurrency } from 'src/enums'
import type {
  SpendChart,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'
import type {
  ReportingChartQuestionContextRef,
  ReportingResolvedChartContext,
} from 'src/queries/reportingAgent/reportingAgent.models'

import type { ReportingArtifact } from './reportingPresentation.types'

interface ReportingChartQuestionContextBase {
  key: string
  title: string
  contextLabel: string
}

export interface ReportingArtifactQuestionContext
  extends ReportingChartQuestionContextBase {
  source: 'artifact'
  artifact: ReportingArtifact
}

export interface ReportingLiveReportQuestionContext
  extends ReportingChartQuestionContextBase {
  source: 'live_report'
  artifact: ReportingArtifact
  reportId?: string
  analysisContextToken?: string
}

export interface ReportingOverviewQuestionContext
  extends ReportingChartQuestionContextBase {
  source: 'overview'
  plan: ChartPlan
  currency: ExtendCurrency
  analysisContextToken?: string
  query: {
    chart: SpendChart
    since: string
    until: string
  }
}

export type ReportingChartQuestionContext =
  | ReportingArtifactQuestionContext
  | ReportingLiveReportQuestionContext
  | ReportingOverviewQuestionContext

export interface ReportingChartQuestionRequest {
  id: string
  question: string
  context: ReportingChartQuestionContext
}

export type AskReportingChartQuestion = (
  context: ReportingChartQuestionContext,
  question: string,
) => void

export const toReportingChartContextRef = (
  context: ReportingChartQuestionContext,
): ReportingChartQuestionContextRef => {
  const display = {
    title: context.title,
    context_label: context.contextLabel,
  }
  if (context.source === 'overview') {
    if (!context.analysisContextToken) {
      throw new Error('overview chart context is missing its analysis token')
    }
    if (context.query.chart === 'spend_by_person') {
      throw new Error('this overview panel is not an embeddable chart context')
    }
    return {
      version: 1,
      source: {
        kind: 'overview',
        analysis_context_token: context.analysisContextToken,
        chart: context.query.chart,
        since: context.query.since,
        until: context.query.until,
        currency: context.currency,
      },
      display,
    }
  }
  if (context.source === 'live_report') {
    if (!context.analysisContextToken || !context.reportId) {
      throw new Error('live report context is missing its source authority')
    }
    return {
      version: 1,
      source: {
        kind: 'live_report',
        analysis_context_token: context.analysisContextToken,
        report_id: context.reportId,
      },
      display,
    }
  }
  const sourceContext = context.artifact.sourceContext
  const originConversationId =
    sourceContext?.kind === 'artifact'
      ? sourceContext.originConversationId
      : undefined
  if (!originConversationId) {
    throw new Error('artifact chart context is missing its origin conversation')
  }
  return {
    version: 1,
    source: {
      kind: 'artifact',
      origin_conversation_id: originConversationId,
      artifact_id: context.artifact.id,
    },
    display,
  }
}

/** Rebuild the embedded context card from the server-safe projection after reload. */
export const toRestoredReportingChartContext = (
  resolved: ReportingResolvedChartContext | null | undefined,
): ReportingChartQuestionContext | undefined => {
  if (!resolved) return undefined
  const presentation = resolved.presentation
  if (presentation.kind === 'overview') {
    const raw = presentation.spend_chart
    const chart: SpendChartView = {
      chart: raw.chart,
      since: raw.since,
      until: raw.until,
      currency: raw.currency as ExtendCurrency,
      totalSpendCents: raw.total_spend_cents,
      points: raw.points.map((point) => ({
        label: point.label,
        spendCents: point.spend_cents,
        key: point.key,
        bucket: point.bucket,
        seriesKey: point.series_key,
        seriesLabel: point.series_label,
        metadata: point.metadata,
      })),
    }
    return {
      source: 'overview',
      key: resolved.chart_context_id,
      title: resolved.title,
      contextLabel: resolved.context_label,
      currency: chart.currency,
      query: {
        chart: chart.chart,
        since: chart.since,
        until: chart.until,
      },
      plan: toReportingStackedBarPlan({
        chart,
        title: resolved.title,
        description: resolved.context_label,
        ariaLabel: `${resolved.title} chart`,
        unknownLabel: 'Unknown',
      }),
    }
  }
  const artifact: ReportingArtifact = {
    id: resolved.chart_context_id,
    title: resolved.title,
    chartSpec: presentation.chart_spec,
    dataset: presentation.dataset,
    sourceContext: { kind: 'artifact', originConversationId: '' },
  }
  return resolved.source_kind === 'live_report'
    ? {
        source: 'live_report',
        key: resolved.chart_context_id,
        title: resolved.title,
        contextLabel: resolved.context_label,
        artifact,
      }
    : {
        source: 'artifact',
        key: resolved.chart_context_id,
        title: resolved.title,
        contextLabel: resolved.context_label,
        artifact,
      }
}
